<script lang="ts">
  import RequestItemSummary from "@shared/svelte/Components/RequestableItems/RequestItemSummary.svelte";
  import RequestWidgetGroup from "@retailer/svelte/CartWidget/v2/RequestWidgetGroup.svelte";
  import { TaskPanel, Alert, Heading, Text } from "@getprovi/craft-svelte";

  import CartStore from "@retailer/svelte/stores/CartStore";
  import { CraftAlertsStore } from "@shared/svelte/Components/Craft/CraftAlertsWrapper.svelte";
  import { InventoryItemStore } from "@retailer/svelte/CartWidget/v2/Stores/InventoryItemStore";

  import { EventTracking } from "@shared/services/event_tracking/events";

  import type { CartProductUpdateParams } from "@retailer/svelte/CartWidget/v2/Services/CartItemRequestService";
  import { RequestItemEntity } from "@shared/services/event_tracking/entities";

  const { trackClick } = EventTracking;

  export let open: boolean;
  export let cartWidgetItem: any;
  export let fulfillmentWarehouseId: number | null = null;
  export let isCartPage: boolean = false;

  let caseQuantity: number =
    cartWidgetItem.buying_options?.find(option => option.quantity_type === "case")?.quantity || 0;
  let unitQuantity: number =
    cartWidgetItem.buying_options?.find(option => option.quantity_type === "unit")?.quantity || 0;

  let distributor = cartWidgetItem.distributor_summary;
  let product = cartWidgetItem.product_summary;
  let showAlert: boolean = false;
  let hasItemsInCart: boolean = false;
  let primaryLabel: string = "Add request to order";
  let disableCartAdd: boolean = true;
  let newCaseQuantity: number = caseQuantity;
  let newUnitQuantity: number = unitQuantity;
  let widgetAtDefaultMax: boolean = false;

  const updateCartQuantity = async () => {
    const cartProduct: CartProductUpdateParams = {
      inventory_id: cartWidgetItem.inventory_id,
      case_quantity: newCaseQuantity || 0,
      unit_quantity: newUnitQuantity || 0,
      requested_item: true,
    };

    cartProduct.warehouse_id = fulfillmentWarehouseId;

    trackClick(
      hasItemsInCart
        ? "request_task_panel_update_request_button"
        : "request_task_panel_add_request_button",
      trackingParams,
    );

    caseQuantity = newCaseQuantity;
    unitQuantity = newUnitQuantity;
    await InventoryItemStore.addToCart(cartWidgetItem.inventory_id, cartProduct);
    if (isCartPage) await CartStore.load(); // need this to properly update prices when adding items on cart page

    open = false;

    primaryLabel =
      newCaseQuantity > 0 || newUnitQuantity > 0 ? "Update request" : "Add request to order";

    if (caseQuantity === 0 && unitQuantity === 0) {
      CraftAlertsStore.add({
        title: "Request removed from cart.",
        message: "",
        variant: "success",
      });
    } else {
      CraftAlertsStore.add({
        title: "Request added to cart.",
        message: "It will be submitted once you send your order.",
        variant: "success",
      });
    }
  };

  const reset = () => {
    disableCartAdd = true;
    newCaseQuantity = caseQuantity;
    newUnitQuantity = unitQuantity;
  };

  $: {
    showAlert = newCaseQuantity > 0 || newUnitQuantity > 0;
    hasItemsInCart = caseQuantity > 0 || unitQuantity > 0;
    primaryLabel = hasItemsInCart ? "Update request" : "Add request to order";
    disableCartAdd = newCaseQuantity === caseQuantity && newUnitQuantity === unitQuantity;
  }

  let trackingParams = [
    new RequestItemEntity({ distributor_id: distributor.id, product_id: product.id }),
  ];
</script>

<TaskPanel
  bind:open
  id="requestable-task-panel"
  testId="requestable-task-panel"
  title="Request Item"
  primaryClick={updateCartQuantity}
  primaryBtnProps={{ disabled: disableCartAdd || widgetAtDefaultMax }}
  secondaryLabel={"Cancel"}
  secondaryClick={() => {
    open = false;
    trackClick("request_task_panel_close_cancel", trackingParams);
    reset();
  }}
  closeBtnClick={() => {
    trackClick("request_task_panel_close_x_button", trackingParams);
    reset();
  }}
  clickOutsideCallback={reset}
  {primaryLabel}
>
  <RequestItemSummary inventoryId={cartWidgetItem.inventory_id} />

  <div class="flex-col space-y-24">
    <div class="flex-col space-y-8">
      <Heading size="h4">We'll send this request with your upcoming order.</Heading>
      <Text size="sm" color="disabled">Choose your desired quantity:</Text>
    </div>

    <RequestWidgetGroup
      {cartWidgetItem}
      bind:newCaseQuantity
      bind:newUnitQuantity
      bind:widgetAtDefaultMax
    />
    {#if showAlert}
      <Alert variant="warning" title="Requested products," testId="request-warning">
        unless fulfilled, won't count towards order minimums or order totals.
      </Alert>
    {/if}
  </div>
</TaskPanel>
